// Plans: names, taglines and feature lines live here. PRICES are edited by a platform admin in
// Admin → Payments → Prices (stored in platform_settings 'pricing', read with the get_pricing RPC);
// DEFAULT_PRICING below is only the fallback until that loads (keep it close to the saved prices).
// After saving new prices, "Apply to Stripe" for each mode replaces the Stripe price (same lookup
// key; current subscribers keep their old price).
// Prices are in USD. `year` is the price per month when billed yearly (shown as "/mo, billed yearly").
// Stripe prices are found by lookup key (carduzo_pro_month, ...); Teams prices are per seat.
// A feature can be a string, or { text, soon: true } to show a "Coming soon" tag.

export const CURRENCY = 'USD';

/** Fallback pricing; the saved one comes from the database (see applyPricing). Same shape as platform_settings 'pricing'. */
export const DEFAULT_PRICING = {
  pro: { month: 7, yearTotal: 60 },
  teams: { month: 6, yearTotal: 60, minSeats: 3 },
  trialDays: 14,
};

// `let` exports are live bindings: importers see the values applyPricing() sets.
/** Free trial on Pro and Teams (card required, once per account). create-checkout reads the same setting. */
export let TRIAL_DAYS = DEFAULT_PRICING.trialDays;

export let TEAM_MIN_SEATS = DEFAULT_PRICING.teams.minSeats;
export const TEAM_MAX_SEATS = 500;

export const PLANS = [
  {
    id: 'free',
    name: 'Free',
    tagline: 'One card, everything you need to share it.',
    price: { month: 0, year: 0 },
    perSeat: false,
    cta: 'Start free',
    features: [
      '1 digital business card',
      'All 5 layouts, with your photo',
      'QR code and share link',
      'Contact download for anyone you meet',
      'Views and saves for the last 30 days',
    ],
  },
  {
    id: 'pro',
    name: 'Pro',
    tagline: 'For people who want to know who they met.',
    price: { month: 7, year: 5 }, // set by applyPricing
    yearTotal: 60,
    perSeat: false,
    highlight: true,
    cta: 'Try Pro free',
    features: [
      'Everything in Free',
      'Lead capture on your card',
      'Leads page with CSV export',
      'Email alert for every new lead',
      'Lead counts in your 30-day stats',
    ],
  },
  {
    id: 'teams',
    name: 'Teams',
    tagline: 'One brand, a card for everyone on the team.',
    price: { month: 6, year: 5 }, // per seat, set by applyPricing
    yearTotal: 60,
    perSeat: true,
    minSeats: TEAM_MIN_SEATS,
    cta: 'Try Teams free',
    features: [
      'Everything in Pro, for each person',
      'Company logo, colors and default layout',
      'Lock the layout so every card stays on brand',
      'Turn lead capture on for the whole team',
      'Team leads in one place',
      'Admins invite and manage people',
    ],
  },
];

export const PAID_PLANS = ['pro', 'teams'];

/* ---------------- Editable pricing ---------------- */

const num = (v, min, max) => {
  const n = Number(v);
  return Number.isFinite(n) && n >= min && n <= max ? Math.round(n * 100) / 100 : null;
};

/** A clean pricing object, or null when anything is missing or out of range. */
export function normalizePricing(p) {
  if (!p || typeof p !== 'object') return null;
  const out = {
    pro: { month: num(p.pro?.month, 0.5, 10000), yearTotal: num(p.pro?.yearTotal, 0.5, 10000) },
    teams: { month: num(p.teams?.month, 0.5, 10000), yearTotal: num(p.teams?.yearTotal, 0.5, 10000), minSeats: num(p.teams?.minSeats, 1, 100) },
    trialDays: num(p.trialDays, 0, 90),
  };
  const ok = [out.pro.month, out.pro.yearTotal, out.teams.month, out.teams.yearTotal, out.teams.minSeats, out.trialDays].every((v) => v !== null);
  if (!ok || !Number.isInteger(out.teams.minSeats) || !Number.isInteger(out.trialDays)) return null;
  return out;
}

let current = DEFAULT_PRICING;
let version = 0;
const listeners = new Set();

/** The pricing in use now (saved one once loaded, else DEFAULT_PRICING). */
export const currentPricing = () => current;
export const pricingVersion = () => version;
export function subscribePricing(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Put saved pricing into PLANS / TRIAL_DAYS / TEAM_MIN_SEATS. Ignores invalid input. */
export function applyPricing(raw) {
  const p = normalizePricing(raw);
  if (!p) return false;
  current = p;
  for (const id of ['pro', 'teams']) {
    const plan = PLANS.find((x) => x.id === id);
    plan.price = { month: p[id].month, year: Math.round((p[id].yearTotal / 12) * 100) / 100 };
    plan.yearTotal = p[id].yearTotal;
  }
  PLANS.find((x) => x.id === 'teams').minSeats = p.teams.minSeats;
  TEAM_MIN_SEATS = p.teams.minSeats;
  TRIAL_DAYS = p.trialDays;
  version += 1;
  listeners.forEach((fn) => fn());
  return true;
}
applyPricing(DEFAULT_PRICING);

/** Stripe amounts in cents by lookup key, for Admin → Payments → Apply to Stripe. */
export function pricingCents(p = current) {
  const c = (n) => Math.round(n * 100);
  return {
    carduzo_pro_month: c(p.pro.month),
    carduzo_pro_year: c(p.pro.yearTotal),
    carduzo_teams_month: c(p.teams.month),
    carduzo_teams_year: c(p.teams.yearTotal),
  };
}

/** Yearly saving vs 12 monthly payments, as a whole percent (for the admin preview). */
export const savingPercent = (month, yearTotal) => (month > 0 && yearTotal > 0 ? Math.round((1 - yearTotal / (month * 12)) * 100) : 0);

export const getPlan = (id) => PLANS.find((p) => p.id === id) || PLANS[0];

/** "$6" / "$0" / "$5.50" */
export const money = (n) => `$${Number.isInteger(n) ? n : n.toFixed(2)}`;

/** What one billing period costs for a plan, interval and seat count. */
export function periodTotal(plan, interval, seats = 1) {
  const qty = plan.perSeat ? Math.max(seats, plan.minSeats || 1) : 1;
  if (interval === 'year') return (plan.yearTotal ?? plan.price.year * 12) * qty;
  return plan.price.month * qty;
}

/** Yearly saving vs paying monthly for 12 months, as a whole percent (0 when none). */
export function yearlySavingPercent(plan) {
  if (!plan.price.month) return 0;
  return Math.max(0, savingPercent(plan.price.month, plan.yearTotal ?? plan.price.year * 12));
}

/** Normalise a feature entry to { text, soon }. */
export const featureOf = (f) => (typeof f === 'string' ? { text: f, soon: false } : { soon: false, ...f });
